interface AstroProps {
  onNavigate: (to: string) => void;
}

interface GameEntry {
  slug: string;
  name: string;
  blurb: string;
  ready: boolean;
}

const GAMES: readonly GameEntry[] = [
  {
    slug: "corrections",
    name: "Sextant corrections",
    blurb:
      "Quick-fire on the Hs → Ho chain. Sun, Moon, planets and stars in the mix, with realistic index error, height of eye, and lower/upper-limb variants. The reveal shows the full working step by step.",
    ready: true,
  },
  {
    slug: "sight-reduction",
    name: "Sight reduction — intercept method",
    blurb:
      "Work the full intercept-method sight: given an AP plus the body's GHA, Dec and your corrected Ho, read off the intercept distance and direction. The reveal shows LHA, Hc and Zn for every question.",
    ready: true,
  },
  {
    slug: "reading",
    name: "Read the sextant scales",
    blurb:
      "Two scales, one reading. Pointer on the arc gives whole degrees; pointer on the micrometer drum gives the minutes and tenths. The skill students get wrong on day one — drill it until it's instant.",
    ready: true,
  },
  {
    slug: "star-id",
    name: "Name the navigational star",
    blurb:
      "The 58 stars the Almanac tabulates. Given the constellation, declination, SHA and magnitude, pick the right name — the recognition skill that makes evening star sights possible.",
    ready: true,
  },
  {
    slug: "spot-error",
    name: "Spot the sextant error",
    blurb:
      "Diagnose the four classical sextant adjustments — perpendicularity, side, index and collimation — from the eyepiece view. The reveal walks through cause and how to adjust the offending mirror.",
    ready: true,
  },
  {
    slug: "almanac",
    name: "Almanac increments & corrections",
    blurb:
      "The RYA \"yellow pages\" workflow — given the hourly GHA, hourly Dec and the v / d values, work out the GHA or Dec at the moment of your sight. Sun, stars, Moon and planets all in the mix.",
    ready: true,
  },
  {
    slug: "abc",
    name: "Azimuth — ABC method",
    blurb:
      "Practice the Norie's-tables ABC azimuth: A from Lat & LHA, B from Dec & LHA, name C by sign, then Z and Zn from the quadrant rules. The reveal shows every named value.",
    ready: true,
  },
];

const TOOLS: readonly { slug: string; name: string; blurb: string }[] = [
  {
    slug: "pro-forma",
    name: "Sight reduction pro forma",
    blurb:
      "Printable RYA-style worksheet. Either print blank and fill in by hand while you work the sight, or punch in your numbers and the form populates every cell so you can check your hand-working line by line.",
  },
];

/// Astronavigation module hub. Phase 1 ships the sextant-correction
/// chain — the workhorse skill students get wrong first. Sight
/// reduction, star ID, and the Moon/planets/twilight finders follow.
export function Astro({ onNavigate }: AstroProps) {
  return (
    <>
      <p className="muted">
        Celestial navigation — the Yachtmaster Ocean syllabus, pitched so
        any navigation student aged 12+ can pick it up. The Reference tab
        has the celestial-sphere primer, the sextant correction
        calculator, and the worked sequences; the drills below get the
        muscle memory in.
      </p>

      <h2 style={{ marginTop: 24 }}>Games</h2>
      {GAMES.map((g) => (
        <div key={g.slug} className="card">
          <h3 style={{ marginBottom: 4 }}>
            {g.name}
            {!g.ready && (
              <span className="pill" style={{ marginLeft: 8, fontSize: 12 }}>
                coming soon
              </span>
            )}
          </h3>
          <p className="small muted" style={{ marginBottom: 12 }}>
            {g.blurb}
          </p>
          {g.ready ? (
            <button
              onClick={() => onNavigate(`/play/astro/${g.slug}`)}
              className="secondary"
            >
              Play
            </button>
          ) : (
            <button disabled className="secondary">
              Not yet
            </button>
          )}
        </div>
      ))}

      <h2 style={{ marginTop: 24 }}>Tools</h2>
      {TOOLS.map((t) => (
        <div key={t.slug} className="card">
          <h3 style={{ marginBottom: 4 }}>{t.name}</h3>
          <p className="small muted" style={{ marginBottom: 12 }}>
            {t.blurb}
          </p>
          <button
            onClick={() => onNavigate(`/play/astro/${t.slug}`)}
            className="secondary"
          >
            Open
          </button>
        </div>
      ))}

      <button className="secondary" onClick={() => onNavigate("/play")}>
        ← Back to Play
      </button>
    </>
  );
}
